/**
 * Retouren v3 (sessie 172): "Contact over deze retour".
 *
 * Per contact de datum gedempt plus een zin. Het berichtvak gebruikt de
 * bestaande weg van de reactietegel op de zaak van deze retour: het
 * concept van Sanne (`["werkplek","concept",zaakId]`) en
 * `werkplekTegelApi.versturen` met ongedaan maken. Notitie via de
 * notitieroute van de zaak, Bellen via het bestaande belvenster,
 * WhatsApp als wa.me-link (alleen met een nummer).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { vertaalApiFout } from "@/lib/api";
import type { RetourV3Dossier } from "@/lib/retouren-v3-api";
import {
  werkplekTegelApi,
  type BelGestart,
  type TegelVerstuurd,
} from "@/lib/werkplek-api";

import { Belbalk } from "../../Tegel/Belbalk";
import { Belvenster } from "../../Tegel/Belvenster";
import "./dossier-v3.css";

function voornaam(naam: string | null): string {
  const eerste = (naam ?? "").trim().split(/\s+/)[0];
  return eerste || "de klant";
}

function waNummer(nummer: string): string {
  const cijfers = nummer.replace(/[^\d+]/g, "");
  if (cijfers.startsWith("+")) return cijfers.slice(1);
  if (cijfers.startsWith("00")) return cijfers.slice(2);
  if (cijfers.startsWith("0")) return `31${cijfers.slice(1)}`;
  return cijfers;
}

function tijd(iso: string): string {
  return new Date(iso).toLocaleTimeString("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Amsterdam",
  });
}

interface Props {
  dossier: RetourV3Dossier;
  /** Telefoon of desktop; de opmaak volgt via .rv3d-smal. */
  smal: boolean;
  /** Id van het berichtvak; de knop Mail op de telefoon springt erheen. */
  veldId: string;
  zorgZaak: () => Promise<string>;
  verversen: () => void;
}

export function DossierContact({ dossier, veldId, zorgZaak, verversen }: Props) {
  const client = useQueryClient();
  const navigeer = useNavigate();
  const zaakId = dossier.zaak_id;
  // Wat JP zelf typte; null = nog niets, dan staat Sannes concept er.
  const [getypt, setGetypt] = useState<string | null>(null);
  const [verstuurd, setVerstuurd] = useState<TegelVerstuurd | null>(null);
  const [dubbel, setDubbel] = useState<string | null>(null);
  const [notitieOpen, setNotitieOpen] = useState(false);
  const [notitie, setNotitie] = useState("");
  const [notitieKlaar, setNotitieKlaar] = useState(false);
  const [belOpen, setBelOpen] = useState(false);
  const [gesprek, setGesprek] = useState<BelGestart | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  // Dezelfde sleutel en route als de reactietegel.
  const concept = useQuery({
    queryKey: ["werkplek", "concept", zaakId],
    queryFn: () => werkplekTegelApi.concept(zaakId as string),
    enabled: Boolean(zaakId),
  });
  const sanneTekst =
    concept.data && concept.data.bron === "sanne" && !concept.data.afgerond
      ? concept.data.tekst
      : "";
  const tekst = getypt ?? sanneTekst;

  function tijdlijnOpnieuw(id: string) {
    void client.invalidateQueries({ queryKey: ["werkplek", "tijdlijn", id] });
    void client.invalidateQueries({ queryKey: ["werkplek", "wachtrij", id] });
  }

  const versturen = useMutation({
    mutationFn: async (bevestigDubbel: boolean) => {
      const id = await zorgZaak();
      const c = concept.data ?? (await werkplekTegelApi.concept(id));
      const uitkomst = await werkplekTegelApi.versturen(id, {
        kanaal: c.kanaal,
        aan: c.aan,
        cc: c.cc,
        bcc: c.bcc,
        onderwerp: c.kanaal === "mail" ? c.onderwerp : null,
        tekst: tekst.trim(),
        stem: c.stem || "sanne",
        moment: "nu",
        sanne_leert: getypt !== null && sanneTekst !== "",
        bevestig_dubbel: bevestigDubbel,
      });
      return { id, uitkomst };
    },
    onSuccess: ({ id, uitkomst }) => {
      setVerstuurd(uitkomst);
      setDubbel(null);
      setFout(null);
      setGetypt("");
      tijdlijnOpnieuw(id);
      void client.invalidateQueries({ queryKey: ["werkplek", "concept", id] });
      verversen();
    },
    onError: (e) => {
      const antwoord = (
        e as { response?: { status?: number; data?: { code?: string; detail?: string } } }
      )?.response;
      if (antwoord?.status === 409 && antwoord.data?.code === "al_verstuurd") {
        setDubbel(antwoord.data.detail ?? "Deze tekst is in dit gesprek al verstuurd.");
        return;
      }
      setFout(vertaalApiFout(e, "Het bericht is niet klaargezet."));
    },
  });

  const ongedaan = useMutation({
    mutationFn: (wachtrijId: string) => werkplekTegelApi.ongedaan(wachtrijId),
    onSuccess: () => {
      setVerstuurd(null);
      if (zaakId) tijdlijnOpnieuw(zaakId);
      verversen();
    },
    onError: (e) => setFout(vertaalApiFout(e, "Ongedaan maken lukte niet meer.")),
  });

  const notitieOpslaan = useMutation({
    mutationFn: async () => {
      const id = await zorgZaak();
      await werkplekTegelApi.notitie(id, notitie.trim());
      return id;
    },
    onSuccess: (id) => {
      setNotitie("");
      setNotitieOpen(false);
      setNotitieKlaar(true);
      setFout(null);
      tijdlijnOpnieuw(id);
      verversen();
    },
    onError: (e) => setFout(vertaalApiFout(e, "De notitie is niet opgeslagen.")),
  });

  const telefoon = dossier.klant_telefoon;
  const naam = voornaam(dossier.klant_naam);

  return (
    <div className="rv3d-contact" data-testid="rv3d-contact">
      <h3 className="rv3d-contact-kop">Contact over deze retour</h3>
      {dossier.contacten.map((c, i) => (
        <div key={`${c.moment ?? c.datum}-${i}`} className="rv3d-contactregel">
          <span className="rv3d-contactdatum">{c.datum}</span>
          <span>{c.zin}</span>
        </div>
      ))}

      <form
        className="rv3d-berichtrij"
        onSubmit={(e) => {
          e.preventDefault();
          if (tekst.trim()) versturen.mutate(false);
        }}
      >
        <label htmlFor={veldId} className="rv3d-verborgen">
          Bericht aan de klant
        </label>
        <textarea
          id={veldId}
          className="rv3d-veld"
          data-testid="rv3d-bericht"
          rows={Math.min(8, Math.max(1, tekst.split("\n").length))}
          placeholder={`Bericht aan ${naam}, Sanne schrijft mee`}
          value={tekst}
          onChange={(e) => {
            setVerstuurd(null);
            setGetypt(e.target.value);
          }}
        />
        <button
          type="submit"
          className="rv3d-knop"
          data-testid="rv3d-versturen"
          disabled={!tekst.trim() || versturen.isPending}
        >
          {versturen.isPending ? "Bezig…" : "Versturen"}
        </button>
      </form>

      {dubbel ? (
        <div className="rv3d-paneel" role="alert">
          <p>{dubbel}</p>
          <div className="rv3d-paneel-knoppen">
            <button
              type="button"
              className="rv3d-knop rv3d-knop-klein"
              onClick={() => versturen.mutate(true)}
            >
              Toch versturen
            </button>
            <button
              type="button"
              className="rv3d-knop rv3d-knop-klein"
              onClick={() => setDubbel(null)}
            >
              Niet versturen
            </button>
          </div>
        </div>
      ) : null}

      {verstuurd ? (
        <div className="rv3d-melding" role="status" data-testid="rv3d-verstuurd">
          Staat klaar, gaat om {tijd(verstuurd.verstuurt_op)}.{" "}
          <button
            type="button"
            className="rv3d-link"
            disabled={ongedaan.isPending}
            onClick={() => ongedaan.mutate(verstuurd.wachtrij_id)}
          >
            Ongedaan maken
          </button>
        </div>
      ) : null}

      <div className="rv3d-contactlinks">
        <button
          type="button"
          className="rv3d-link"
          data-testid="rv3d-notitie"
          aria-expanded={notitieOpen}
          onClick={() => {
            setNotitieKlaar(false);
            setNotitieOpen((aan) => !aan);
          }}
        >
          Notitie
        </button>
        {telefoon ? (
          <button
            type="button"
            className="rv3d-link"
            data-testid="rv3d-bellen"
            aria-expanded={belOpen}
            onClick={() => setBelOpen((aan) => !aan)}
          >
            Bellen
          </button>
        ) : null}
        {telefoon ? (
          <a
            href={`https://wa.me/${waNummer(telefoon)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="rv3d-whatsapp"
          >
            WhatsApp
          </a>
        ) : null}
      </div>

      {notitieOpen ? (
        <form
          className="rv3d-paneel"
          data-testid="rv3d-notitie-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (notitie.trim()) notitieOpslaan.mutate();
          }}
        >
          <label className="rv3d-veldlabel">
            Notitie bij de zaak, niet voor de klant
            <textarea
              className="rv3d-veld"
              rows={2}
              value={notitie}
              onChange={(e) => setNotitie(e.target.value)}
            />
          </label>
          <div className="rv3d-paneel-knoppen">
            <button
              type="submit"
              className="rv3d-knop rv3d-knop-klein"
              data-testid="rv3d-notitie-opslaan"
              disabled={!notitie.trim() || notitieOpslaan.isPending}
            >
              {notitieOpslaan.isPending ? "Bezig…" : "Opslaan"}
            </button>
            <button
              type="button"
              className="rv3d-knop rv3d-knop-klein"
              onClick={() => setNotitieOpen(false)}
            >
              Annuleren
            </button>
          </div>
        </form>
      ) : null}
      {notitieKlaar ? (
        <div className="rv3d-melding" role="status">
          Notitie opgeslagen.
        </div>
      ) : null}

      {gesprek ? (
        <Belbalk
          gesprek={gesprek}
          onKlantpagina={(klantId) => navigeer(`/klanten/${klantId}`)}
          onSluiten={() => {
            setGesprek(null);
            setBelOpen(false);
          }}
        />
      ) : belOpen && telefoon ? (
        <Belvenster
          zaakId={zaakId}
          klantId={dossier.klant_id}
          nummer={telefoon}
          bron="klantblok"
          onGestart={(nieuw) => setGesprek(nieuw)}
          onSluiten={() => setBelOpen(false)}
        />
      ) : null}

      {fout ? (
        <div className="rv3d-fout" role="alert">
          {fout}
        </div>
      ) : null}
    </div>
  );
}
